import React, { useEffect, useMemo, useState } from 'react';

import { Dropdown } from '@/components/Dropdown';
import { Loader } from '@/components/Loader';
import { POE } from '@/sections/poe';
import { Asset, Port, Technology } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';
import { QueryStatus } from '@/utils/api';

interface ImpactedAssetPort {
  asset: Asset;
  port: Port;
  visited: string;
}

export interface TechnologyEvidenceProps {
  technology: Technology;
  impactedAssetsTreeStatus: QueryStatus;
  impactedAssetPorts: ImpactedAssetPort[];
}

export const TechnologyEvidence: React.FC<TechnologyEvidenceProps> = ({
  technology,
  impactedAssetsTreeStatus,
  impactedAssetPorts,
}) => {
  const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(
    null
  );

  const getLabel = (assetPort: ImpactedAssetPort) => {
    return `${assetPort.asset.identifier} - ${assetPort.asset.group} (${assetPort.port.port})`;
  };

  const options = useMemo(() => {
    return impactedAssetPorts.map(assetPort => ({
      label: getLabel(assetPort),
      value: assetPort.port.protocol,
    }));
  }, [impactedAssetPorts]);

  useEffect(() => {
    if (options.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Initialize selection when options first load
      setSelectedOption(options[0]);
    }
  }, [options]);

  const selectedAssetPort = useMemo(() => {
    if (!selectedOption) return null;
    return impactedAssetPorts.find(
      assetPort =>
        assetPort.port.protocol === selectedOption.value &&
        getLabel(assetPort) === selectedOption.label
    );
  }, [selectedOption, impactedAssetPorts]);

  const getPOELocation = (tcpOrUdp: 'tcp' | 'udp') => {
    return `${technology.cpe}/${selectedAssetPort?.asset.group}/${selectedAssetPort?.asset.identifier}/${tcpOrUdp}/${selectedAssetPort?.port.port}`;
  };

  return (
    <Loader
      isLoading={impactedAssetsTreeStatus === 'pending'}
      className="m-6 w-[calc(100%-48px)]"
    >
      <div className="pb-6">
        <Dropdown
          className="mr-2"
          label={
            selectedOption?.label || options[0]?.label || 'Select an asset'
          }
          size="sm"
          styleType="rounded"
          menu={{
            items: options.map(option => ({
              label: option.label,
              value: option.value,
              onClick: () => setSelectedOption(option),
            })),
            placement: 'bottom-start',
          }}
        />
        <span className="text-chariot-text-secondary">
          Select an asset impacted
        </span>
      </div>
      {selectedAssetPort && (
        <POE
          className="max-h-[calc(100vh-380px)] overflow-y-auto"
          poeLocation={getPOELocation('tcp')}
          fallbackPOELocation={getPOELocation('udp')}
          name={technology.name}
          source=""
        />
      )}
    </Loader>
  );
};
